import React, { useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import { DetailRows, KIND_LABELS, labelize, splitRecord } from "../recordFields";
import { AccountLogo } from "Pages/Dashboard/signals/AccountLogo";

// A single record (get_contact, get_sequence, get_task, …) shown inline.
//
// Same field rendering as the side panel — both call DetailRows — just
// collapsed to a preview so one record doesn't flood the thread. The block
// carries the record in the SAME `items` shape entity_cards uses, so
// "Open full record" drives the existing entity panel with no store changes
// and no second full-record view.
const PREVIEW_ROWS = 5;

// Records with their own page open it ("Open full record" read as a promise of the record, not a field list).
const RECORD_PAGES = {
  contact: id => `/contacts/${id}/activity`,
  account: id => `/accounts/${id}`,
  sequence: id => `/sequences/all/${id}`
};

const RecordDetailBlock = observer(({ block }) => {
  const [expanded, setExpanded] = useState(false);
  const history = useHistory();
  const item = (block.items || [])[0];
  if (!item) return null;

  const record = item.detail || {};
  const { rows, sections } = splitRecord(record);
  if (!rows.length && !sections.length) return null;

  const hiddenCount = Math.max(0, rows.length - PREVIEW_ROWS) + sections.length;

  return (
    <div className="copilotRecord">
      <div className="copilotRecord__header">
        {/* Accounts show their logo (from the website), like Signals and the account pages. */}
        {block.kind === "account" && (record.domain || record.website) && (
          <AccountLogo domain={record.domain || record.website} name={item.title} />
        )}
        <div className="copilotRecord__headMeta">
          <div className="copilotRecord__title">{item.title}</div>
          <div className="copilotRecord__kind">
            {KIND_LABELS[block.kind] || labelize(block.kind || "record")}
            {item.subtitle ? ` · ${item.subtitle}` : ""}
          </div>
        </div>
        {item.badges && item.badges.length > 0 && (
          <div className="copilotRecord__badges">
            {item.badges.slice(0, 3).map(badge => (
              <span className="copilotRecord__badge" key={badge}>
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="copilotRecord__body copilotEntityDetail">
        {/* The header already carries name, role and status. */}
        <DetailRows
          record={record}
          kind={block.kind}
          limit={expanded ? 0 : PREVIEW_ROWS}
          skipHeaderFields
        />
      </div>

      <div className="copilotRecord__actions">
        {hiddenCount > 0 && (
          <button
            type="button"
            className="copilotRecord__link"
            aria-expanded={expanded}
            onClick={() => setExpanded(v => !v)}
          >
            {expanded
              ? "Show less"
              : `Show ${hiddenCount} more field${hiddenCount === 1 ? "" : "s"}`}
          </button>
        )}
        <button
          type="button"
          className="copilotRecord__link"
          onClick={() => {
            const recordId = (item.detail || {}).id || item.id;
            const page = RECORD_PAGES[block.kind];
            if (page && recordId) history.push(page(recordId));
            else copilotStore.openEntityPanel(block.id, item.id);
          }}
        >
          Open full record
        </button>
      </div>
    </div>
  );
});

export default RecordDetailBlock;
